import {ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, Optional} from '@angular/core';
import {Location} from '@angular/common';
import {ActivatedRoute, CanDeactivate} from '@angular/router';
import {Subscription} from 'rxjs';
import {DragulaService} from 'ng2-dragula';
import {NgbModal, NgbPopover} from '@ng-bootstrap/ng-bootstrap';

import {MediaCenter} from '@ngmedax/asset';
import {LayoutService} from '@ngmedax/layout';
import {ConfigService} from '@ngmedax/config';
import {SimuService} from '@ngmedax/simu';
import {Translatable, TranslationEventService, TranslationService} from '@ngmedax/translation';
import {flagIconCodeMap, languages} from '@ngmedax/cloud-translator';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';

import {dragulaScroll, dragulaScrollStop} from './scripts/dragula-scroll.script';
import {QuestionnaireEditorService} from './services/questionnaire-editor.service';
import {QuestionnaireStateService} from './services/questionnaire-state.service';
import {QuestionnaireTabsService} from './services/questionnaire-tabs.service';
import {QuestionnaireMediaCenterService} from './services/questionnaire-media-center.service';
import {DomHelperService} from './services/dom-helper.service';
import {CONTAINER_FORMAT_TO_COMPONENT_MAPPINGS} from '../mappings';
import {FROALA_LICENSE_KEY} from './licences/froala';
import {Filtergroup} from '../types';
import {TRANSLATION_EDITOR_SCOPE} from '../constants';
import {KEYS} from '../translation-keys';
import {Question} from '../structure';
import {ApiService} from './services/api.service';
import * as sha from 'js-sha256';
import {VariablesModalComponent} from './components/question/variables/modal/variables-modal.component';
import {QuestionnaireVariablesService} from './services/questionnaire-variables.service';
import {QuestionnaireConditionService} from './services/questionnaire-condition.service';
import {FroalaCodeViewFlushService} from './froala/froala-code-view-flush.service';
import {FormulaValidationService} from './services/formula-validation.service';
import {ToastService} from '../../../toast';

// hack to inject decorator declarations. must occur before class declaration!
export interface QuestionnaireEditorComponent extends Translatable {}
declare const $: any;

@Component({
  selector: 'app-questionnaire-editor',
  templateUrl: './questionnaire-editor.component.html',
  styleUrls: [
    './questionnaire-editor.component.css',
    './shared/reusable.css'
  ]
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class QuestionnaireEditorComponent implements OnInit, OnDestroy, CanDeactivate<QuestionnaireEditorComponent> {

  /**
   * Default froala wysiwyg editor options
   */
  public defaultEditorOptions: any = {
    key: FROALA_LICENSE_KEY,
    placeholderText: 'Beschreibung eingeben...',
    charCounterCount: true,
    quickInsertButtons: [],
    quickInsertTags: [],
    imagePaste: false,
    pluginsEnabled: ['paragraphFormat', 'charCounter'],
    toolbarButtons: ['bold', 'italic', 'underline', 'paragraphFormat'],
    lineWrapping: true,
    language: 'de',
    attribution: false
  };

  /**
   * Questionnaire data object
   * @type {Questionnaire}
   */
  public questionnaire: Questionnaire;

  /**
   * JSON snapshot of questionnaire. Used to detect if questionnaire
   * contains unsaved content since last init/load/save action
   */
  public questionnaireSnapshot: string;

  /**
   * Questionnaire path hash map. contains all hashes
   * with pointers to elements in the questionnaire
   */
  public pathHashMap: any = {};

  /**
   * Locale for questionnaire. Hardcoded to "de_DE" for now.
   * We need to change this, when we implement multi language support
   * @type {string}
   */
  public locale = 'de_DE';

  /**
   * Mappings from container format to components
   * @type {any}
   */
  public mappings = CONTAINER_FORMAT_TO_COMPONENT_MAPPINGS;

  /**
   * Filtergroups
   *
   * @type {[{}]}
   */
  public filtergroups: Filtergroup[];

  /**
   * Options for filtergroup select
   */
  public filtergroupSelectOpts = {
    width: '100%',
    placeholder: '...',
    sorter: (data) => {
      return data.sort((a, b) => {
        if (a.id > b.id) {return 1; }
        if (a.id < b.id) {return -1; }
        return 0;
      });
    }
  };

  /**
   * Storage for questionnaire elements (questions and other elements)
   * @type {Array}
   */
  public elements: any[] = [];

  /**
   * Is the input/output overlay visible?
   * @type {boolean}
   */
  public ioOverlayVisible = false;

  /**
   * Is the translation overlay visible?
   */
  public translationOverlayVisible = false;

  /**
   * Is the ai assistant overlay visible?
   */
  public aiOverlayVisible = false;

  /**
   * Are we in save mode?
   * @type {boolean}
   */
  public saveInProgress = false;

  /**
   * Feature object to determine, what feature is active
   * @type {any}
   */
  public feature: any = {
    editor: {
      patient: {
        upload: false
      }
    },
    pdf: {
      forms: false
    }
  };

  /**
   * Flag to determine if cloud translation is enabled
   * @type {boolean}
   */
  public canCloudTranslate = false;

  /**
   * Is the ai assistant feature unlocked for this user session?
   * @type {boolean}
   */
  public aiAssistantEnabled = false;

  /**
   * Emulated container for meta title (used to set gdt variable)
   * @type {any}
   */
  public metaTitleContainer = {pathHash: sha.sha256('meta.title')};

  /**
   * Flag icon code map
   */
  public flagIconCodeMap = flagIconCodeMap;

  /**
   * Languages
   */
  public languages = languages;

  /**
   * Is single instance multi user mode enabled?
   */
  public isSimu = false;

  /**
   * Page view mode toggle (default: disabled)
   */
  public pageViewEnabled = false;

  /**
   * Flag to prevent event handlers from interfering with page operations
   */
  private isPageOperationInProgress = false;

  /**
   * Current page index (0-based)
   */
  public currentPageIndex = 0;

  /**
   * Select2 options for page dropdown
   */
  public pageSelectOptions = {width: '300px', placeholder: '...'};

  /**
   * Cached pages array (rebuilt when questions change)
   */
  public pages: {startIndex: number; endIndex: number; title: string}[] = [];

  /**
   * Cached visible questions array (rebuilt when page changes)
   */
  public visibleQuestions: {question: any; realIndex: number; isFirstOnPage: boolean; isLastOnPage: boolean}[] = [];

  /**
   * Subscriptions that we must unsubscribe from when component gets destroyed
   */
  private subscriptions: Subscription[] = [];

  /**
   * Are we in edit mode?
   * @type {boolean}
   */
  private editMode = false;

  /**
   * Storage for current drag/drop pos
   */
  private dragPos = {old: 0, new: 0};

  /**
   * Observer that logs long main thread tasks (> 50ms) for performance analysis
   */
  private longTaskObserver: PerformanceObserver = null;

  /**
   * Cache for the broken formula check (validation compiles, so don't run it on every change detection)
   */
  private brokenFormulaCache: {key: string, names: string[]} = {key: null, names: []};

  /**
   * Injects dependencies, deletes previous dragula element bags and translates froala placeholder text
   */
  public constructor(
    public mediaCenter: QuestionnaireMediaCenterService,
    public tabs: QuestionnaireTabsService,
    public domHelper: DomHelperService,
    private editor: QuestionnaireEditorService,
    private state: QuestionnaireStateService,
    private configService: ConfigService,
    private layoutService: LayoutService,
    private simuService: SimuService,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private dragula: DragulaService,
    private ref: ChangeDetectorRef,
    private api: ApiService,
    private modal: NgbModal,
    private toast: ToastService,
    private variables: QuestionnaireVariablesService,
    private conditions: QuestionnaireConditionService,
    private codeViewFlush: FroalaCodeViewFlushService,
    private formulaValidation: FormulaValidationService,
    @Optional() private translationService: TranslationService,
    @Optional() private translationEvents: TranslationEventService,
  ) {
    this.canCloudTranslate = this.configService.get('translation.cloud.active') == true;
    this.isSimu = this.simuService.isSimuMode();

    // the ai assistant button only appears when the secret matches and the backend
    // reports the feature as configured and reachable
    if (this.checkAiAssistantUnlocked()) {
      this.api.getAiStatus()
        .then((response: any) => this.aiAssistantEnabled = !!(response && response.active))
        .catch(() => this.aiAssistantEnabled = false);
    }

    /**
     * Bugfix for dragula: Deletes previous elements bag on progressive page reloads
     */
    for (const bag of ['elements-bag', 'images-bag']) {
      this.dragula.find(bag) && this.dragula.destroy(bag);
    }

    const updatePlaceholderText = () => this.defaultEditorOptions.placeholderText = this._(KEYS.EDITOR.ENTER_DESCRIPTION);

    this.translationEvents && this.translationEvents.onLocaleChanged().subscribe(() => {
      updatePlaceholderText();
      this.ref.markForCheck();
      this.ref.detectChanges();

      this.defaultEditorOptions.language = this.translationService.getLocaleCountryCode(
        this.translationService.getLocale()).toLowerCase();

      // hack to force update of question translations
      const questions = this.questionnaire.questions;
      this.questionnaire.questions = [];
      setTimeout(() => {
        this.questionnaire.questions = questions;

        if (this.pageViewEnabled) {
          this.rebuildPages();
        }
      }, 100);
    });

    updatePlaceholderText();
  }

  /**
   * Returns true when the ai assistant is unlocked: a secret must be configured
   * (feature.editor.aiAssistant.secret) and the url must contain a matching
   * "aiAssistant" query param. A successful unlock is remembered for the browser
   * session, since query params get lost on in-app navigation.
   */
  private checkAiAssistantUnlocked(): boolean {
    const secret = this.configService.get('feature.editor.aiAssistant.secret');

    if (!secret) {
      return false;
    }

    const storageKey = 'qaAiAssistantSecret';

    if (sessionStorage && sessionStorage.getItem(storageKey) === secret) {
      return true;
    }

    const urlParam = new URLSearchParams(window.location.search).get('aiAssistant');

    if (urlParam === secret) {
      sessionStorage && sessionStorage.setItem(storageKey, secret);
      return true;
    }

    return false;
  }

  /**
   * Initializes some subscriptions, froala wysiwyg editor and dragula dnd
   */
  public ngOnInit() {
    // shows preloader
    this.layoutService.showPreloader();

    // start perf logging for MYM-1029
    this.initLongTaskLogging();

    // initialize custom field definitions for conditions and variable mappings
    this.conditions.initCustomFields();
    this.variables.initCustomFieldMappings();

    // get editor features
    this.feature.editor = this.configService.get('feature.editor') || this.feature.editor;
    this.feature.pdf.forms = this.configService.get('feature.pdf.forms') || this.feature.pdf.forms;

    // load filter groups and change select2 selection on questionnaire loading
    const filtergroupChange = this.state.onSetQuestionnaire.subscribe(async (questionnaire) => {
      this.filtergroups = await this.editor.loadFiltergroups();
    });

    this.subscriptions.push(filtergroupChange);

    if (document && document.location && document.location.pathname && !document.location.pathname.match(/editor\/.*$/)) {
      this.applyRestrictions();
    }

    // init a new questionnaire and link it to the state service
    this.startSession(this.editor.initQuestionnaire(this.pathHashMap));

    // only allow dragging via elements that contain the class: qa-handle-control
    // in page view: prevent dropping after the pageBreak element (last position)
    this.dragula.createGroup('elements-bag', {
      moves: (el, container, handle) => handle.classList.contains('qa-handle-control'),
      accepts: (el, target, source, sibling) => {
        // in page view, don't allow dropping at the end (after pageBreak)
        if (this.pageViewEnabled && sibling === null) {
          return false;
        }
        return true;
      }
    });

    // auto scroll the editor, when dragging an element
    this.subscriptions.push(this.dragula.drag('elements-bag').subscribe((opts) => {
      const domIndex = [...<any>opts.el.parentNode.children].indexOf(opts.el);
      this.dragPos.old = domIndex + this.getPageStartIndex();
      dragulaScroll();
    }));

    // trigger change detection and stop scrolling when element was dropped
    this.subscriptions.push(this.dragula.drop('elements-bag').subscribe((opts) => {
      const domIndex = [...<any>opts.target.children].indexOf(opts.el);
      this.dragPos.new = domIndex + this.getPageStartIndex();
      this.state.changeElementPosition(this.dragPos.old, this.dragPos.new);
      dragulaScrollStop();
    }));

    // stop scrolling if cancel event was triggered
    this.subscriptions.push(this.dragula.cancel('elements-bag').subscribe(dragulaScrollStop));

    // rebuild pages when elements change (only if page view is enabled)
    // skip if a page operation is in progress (addNewPage, copyCurrentPage, deleteCurrentPage handle their own logic)
    this.subscriptions.push(this.state.onAddElement.subscribe((change) => {
      if (!this.pageViewEnabled || this.isPageOperationInProgress) {
        return;
      }

      const savedIndex = this.currentPageIndex;
      const oldPagesCount = this.pages.length;
      const currentPage = this.pages[savedIndex];
      const wasInsertedAtEnd = currentPage && change?.position >= currentPage.endIndex;

      this.rebuildPages();

      // when a pageBreak was inserted mid-page, a new page is created before the current one
      const isPageBreakInsert = change?.element?.format === 'pageBreak';
      const newPageCreated = this.pages.length > oldPagesCount;
      const newIndex = (isPageBreakInsert && newPageCreated && !wasInsertedAtEnd) ? savedIndex + 1 : savedIndex;

      this.forceSelectPage(Math.min(newIndex, this.pages.length - 1));
    }));
    this.subscriptions.push(this.state.onDeleteElement.subscribe(() => {
      if (!this.pageViewEnabled || this.isPageOperationInProgress) {
        return;
      }
      const savedIndex = this.currentPageIndex;
      this.rebuildPages();
      this.forceSelectPage(Math.min(savedIndex, this.pages.length - 1));
    }));
    this.subscriptions.push(this.state.onElementPositionChange.subscribe(() => {
      if (!this.pageViewEnabled || this.isPageOperationInProgress) {
        return;
      }
      const savedIndex = this.currentPageIndex;
      this.rebuildPages();
      this.forceSelectPage(savedIndex);
    }));
    this.subscriptions.push(this.state.onUpdateElement.subscribe((change) => {
      if (!this.pageViewEnabled || this.isPageOperationInProgress) {
        return;
      }
      if (change.element?.format === 'pageBreak') {
        const savedIndex = this.currentPageIndex;
        this.rebuildPages();
        this.forceSelectPage(savedIndex);
      }
    }));

    // handle move-to-page event from question components
    this.subscriptions.push(this.state.onMoveToPage.subscribe((targetPageIndex: number) => {
      this.rebuildPages();
      this.forceSelectPage(Math.min(targetPageIndex, this.pages.length - 1), true);
    }));

    /**
     * Load questionnaire by params.id on routing event
     */
    const routeSubscription = this.activatedRoute.params.subscribe(async (params: {id: string}) => {
      await this.loadQuestionnaireById(params.id);
      this.applySimuRestrictions();
    });

    this.subscriptions.push(routeSubscription);
    this.askResumeSession();
  }

  /**
   * Unsubscribe from all subscriptions
   */
  public ngOnDestroy() {
    this.memorizeSession();

    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }

    this.longTaskObserver && this.longTaskObserver.disconnect();
  }

  public getLangCodes(): string[] {
    if (this.questionnaire && this.questionnaire.meta && this.questionnaire.meta.title) {
      return Object.keys(this.questionnaire.meta.title).filter(locale => this.languages[locale]);
    }
    return [];
  }

  public translateLanguage(language: string): string {
    return this.translationService ?
      this.translationService.translate({
        text: language,
        scope: ['cloudTranslatorModule', 'default']
      }) : language;
  }

  /**
   * Memorized questionnaire on page leave
   */
  @HostListener('window:beforeunload', ['$event'])
  memorizeQuestionnaireOnPageLeave(event: BeforeUnloadEvent) {
    this.memorizeSession();

    // Show confirmation dialog if there are unsaved changes
    if (this.isDirty() && this.questionnaire.questions.length > 0) {
      // Modern browsers show their own message regardless of what we set
      // Just prevent default and set returnValue to trigger the dialog
      event.preventDefault();
      event.returnValue = '';
      return '';
    }
  }

  /**
   * Trigger save on Ctrl/Cmd + S.
   * Skips questionnaire save when a modal is open, as modals handle Ctrl+S themselves.
   */
  @HostListener('window:keydown', ['$event'])
  onGlobalSaveShortcut(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && (event.key === 's' || event.key === 'S')) {
      event.preventDefault();

      if (this.modal.hasOpenModals()) {
        return;
      }

      if (!this.saveInProgress) {
        this.onSaveQuestionnaire();
      }
    }
  }

  /**
   * Loads questionnaire by given id
   *
   * @param id
   */
  public async loadQuestionnaireById(id: string) {
    if (!id) {
      // hides preloader
      this.layoutService.hidePreloader();
      return;
    }

    // id given, so we are in edit mode
    this.editMode = true;

    // shows preloader
    this.layoutService.showPreloader();

    try {
      const loadStart = performance.now();
      this.startSession(await this.editor.loadQuestionnaire(id, this.pathHashMap));
      this.logRenderPerf(loadStart);
      this.layoutService.hidePreloader();
      this.notifyOnBrokenFormulas();
    } catch (error) {
      alert(this._(KEYS.EDITOR.ERROR_LOADING_QUESTIONNAIRE));
      console.log(error);
    }
  }

  /**
   * Logs load and initial render time of the questionnaire to the console.
   * Waits two animation frames before measuring the render time, so that
   * component creation, froala initialization and the first paint are included.
   * Perf logging for MYM-1029, helps to compare timings before/after optimizations.
   *
   * @param {number} loadStart Timestamp taken before the questionnaire was loaded
   */
  private logRenderPerf(loadStart: number) {
    const loadEnd = performance.now();
    const numElements = (this.questionnaire && this.questionnaire.questions) ? this.questionnaire.questions.length : 0;
    console.log(`[qa-perf] load total: ${Math.round(loadEnd - loadStart)}ms (${numElements} elements)`);

    // static lazy froala previews reuse the fr-box class for styling,
    // so only count boxes with a real (contenteditable) froala instance
    const realEditorSelector = '.fr-box:not(.qa-lazy-froala-static)';

    requestAnimationFrame(() => requestAnimationFrame(() => {
      const numEditors = document.querySelectorAll(realEditorSelector).length;
      const numPreviews = document.querySelectorAll('.qa-lazy-froala-static').length;
      console.log(`[qa-perf] initial render: ${Math.round(performance.now() - loadEnd)}ms, `
        + `froala editors: ${numEditors}, lazy previews: ${numPreviews}`);
    }));

    // delayed measurements to see how many question components and froala editors
    // exist once the editor has settled after the initial load
    for (const delay of [5000, 15000]) {
      setTimeout(() => {
        const numQuestions = document.querySelectorAll('.qa-question').length;
        const numEditors = document.querySelectorAll(realEditorSelector).length;
        const numPreviews = document.querySelectorAll('.qa-lazy-froala-static').length;
        console.log(`[qa-perf] after ${delay / 1000}s: ${numQuestions} question components, `
          + `${numEditors} froala editors, ${numPreviews} lazy previews`);
      }, delay);
    }
  }

  /**
   * Starts logging of long main thread tasks (> 50ms) to the console.
   * Useful to measure editor responsiveness (e.g. while typing or hovering).
   * Perf logging for MYM-1029, helps to compare timings before/after optimizations.
   */
  private initLongTaskLogging() {
    try {
      this.longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          console.log(`[qa-perf] long task: ${Math.round(entry.duration)}ms`);
        }
      });
      this.longTaskObserver.observe({entryTypes: ['longtask']});
    } catch (error) {
      // long task api not supported by this browser. perf logging is optional, so we ignore this
      this.longTaskObserver = null;
    }
  }

  /**
   * Event handler for when we should save the questionnaire
   */
  public async onSaveQuestionnaire() {
    await this.codeViewFlush.flushAll();

    if (!this.questionnaire || !this.questionnaire.meta || !this.questionnaire.meta.title[this.locale]) {
      alert(this._(KEYS.EDITOR.PLEASE_INSERT_TITLE_TO_SAVE));
      return;
    }

    const hasNoFilterGroups = !this.filterTags(this.questionnaire.meta.tags || []).length;
    if (this.isSimu && hasNoFilterGroups) {
      alert(this._(KEYS.EDITOR.PLEASE_INSERT_FILTER_GROUP_TO_SAVE));
      return;
    }

    this.saveInProgress = true;

    try {
      await this.editor.saveQuestionnaire(this.editMode);

      /**
       * NOTE: The components can alter the questionnaire when rendering it. Therefore we have to wait a moment.
       * otherwise the "is dirty" check might not work correctly...
       */
      this.questionnaire = await this.editor.loadQuestionnaire(this.questionnaire.id, this.pathHashMap);
      await new Promise(resolve => setTimeout(resolve, 200));

      this.startSession(this.questionnaire);
      this.editMode = true;
      this.saveInProgress = false;
      alert(this._(KEYS.EDITOR.SUCCESSFULLY_SAVED_QUESTIONNAIRE));
    } catch (error) {
      this.saveInProgress = false;
      alert(this._(KEYS.EDITOR.ERROR_SAVING_QUESTIONNAIRE));
      console.error(error);
    }
  }

  /**
   * Returns names of formula variables whose expression does not validate.
   * Cached by the questionnaire variables, as this runs from the template
   *
   * @returns {string[]}
   */
  public getBrokenFormulaNames(): string[] {
    const variables: any = (this.questionnaire && this.questionnaire.meta && this.questionnaire.meta.options
      && this.questionnaire.meta.options.variables) || {};
    const formulaVars: any = variables['formula'] || {};
    const key = JSON.stringify(variables);

    if (this.brokenFormulaCache.key === key) {
      return this.brokenFormulaCache.names;
    }

    const names = Object.keys(formulaVars).filter(name => {
      const expression = (formulaVars[name] && formulaVars[name].expression) || '';
      return !!this.formulaValidation.validate(expression + '', this.questionnaire);
    });

    this.brokenFormulaCache = {key, names};
    return names;
  }

  /**
   * Returns true when the questionnaire contains broken formulas (marks the formula tab)
   *
   * @returns {boolean}
   */
  public hasBrokenFormulas(): boolean {
    return this.getBrokenFormulaNames().length > 0;
  }

  /**
   * Shows a toast when the loaded questionnaire contains broken formulas
   */
  private notifyOnBrokenFormulas() {
    const brokenNames = this.getBrokenFormulaNames();

    if (brokenNames.length) {
      this.toast.error(`${this._(KEYS.EDITOR.FORMULA_BROKEN_TOAST)}: ${brokenNames.join(', ')}`);
    }
  }

  /**
   * Opens media center modal
   *
   * @param mediaType
   * @param containerAsset
   */
  public onOpenMediaCenterModal(containerAsset: Questionnaire.Container.Asset = null) {
    const locale = this.locale;
    const questionnaireId =  this.state.getQuestionnaire().id;
    const buildAsset = this.mediaCenter.buildContainerAsset;
    const maintainAspectRatio = true;
    const aspectRatio = 16 / 3.8;

    const callback = (mediaCenterAsset: MediaCenter.Asset) => {
      containerAsset = buildAsset({mediaCenterAsset, containerAsset, locale});
      this.questionnaire.meta.asset.image = containerAsset;
    };

    this.mediaCenter.openMediaCenter({
      questionnaireId, mediaType: 'images', locale, containerAsset, callback, maintainAspectRatio, aspectRatio
    });
  }

  /**
   * Deleted meta asset image object of this questionnaire
   */
  public onDeleteImage() {
    if (!this.questionnaire.meta.asset.image) {
      return;
    }

    this.editor.confirmDelete(() => delete(this.questionnaire.meta.asset.image));
  }

  /**
   * Jumps to question
   *
   * @param popover
   */
  public onJumpToQuestion(popover: NgbPopover) {
    $('ngb-popover-window').hide();

    // wait a moment, so we don't hide the new popover
    setTimeout(() => {
      popover.close();
      popover.autoClose = false;
      popover.open();
    }, 100);
  }

  /**
   * Opens modal for gdt meta title variables
   */
  public onGdtMetaTitleVariablesModal() {
    const modalRef = this.modal.open(VariablesModalComponent, {size: 'lg'});
    modalRef.componentInstance.container = this.metaTitleContainer;
    modalRef.componentInstance.disabledScopes = {upload: true, inline: true, pdf: true, client: true, fhir: true, 'pdf-form': true};
  }


  /**
   * Opens modal for gdt meta formula variables
   *
   * @param {string} formulaVarName
   */
  public onGdtMetaFormulaVariablesModal(formulaVarName: string) {
    const virtualContainer = {pathHash: sha.sha256(`formula.${formulaVarName}`)};
    const modalRef = this.modal.open(VariablesModalComponent, {size: 'lg'});
    modalRef.componentInstance.container = virtualContainer;
    modalRef.componentInstance.disabledScopes = {upload: true, inline: true, pdf: true, client: true, fhir: true, 'pdf-form': true};
  }

  /**
   * Adds default gdt formula variables, if not already added.
   * Uses gdt key 8480 by default (result text)
   *
   */
  public addDefaultGdtFormulaVariables() {
    if (!this.questionnaire.meta.options.gdt || !this.questionnaire.meta.options.gdt.addFormulaResults) {
      return;
    }

    const formulaPathHashes = Object.keys(this.questionnaire.meta.options.variables['formula'] || {}).map(varName => sha.sha256(`formula.${varName}`));

    if (!formulaPathHashes.length) {
      return;
    }

    const gdtVariables = this.variables.getVariablesForScope('gdt');
    const flipped = this.variables.flipVariables(gdtVariables);

    for (const formulaPathHash of formulaPathHashes) {
      if (flipped[formulaPathHash]) {
        continue;
      }

      this.variables.addVariableMapping('gdt', '8480', formulaPathHash, {});
    }
  }

  /**
   * Returns true when questionnaire contains formula variables
   * @returns {boolean}
   */
  public hasFormulaVariables(): boolean {
    const formula = this.questionnaire.meta.options.variables['formula'];
    return formula && Object.keys(formula).length > 0
  }

  /**
   * Returns formula variables as array
   * @returns {string[]}
   */
  public getFormulaVariables(): string[] {
    return Object.keys(this.questionnaire.meta.options.variables['formula'] || {});
  }

  /**
   * Opens modal for gdt meta scoring variables
   *
   * @param {string} scoringVarName
   */
  public onGdtMetaScoringVariablesModal(scoringVarName: string) {
    const virtualContainer = {pathHash: sha.sha256(scoringVarName)};
    const modalRef = this.modal.open(VariablesModalComponent, {size: 'lg'});
    modalRef.componentInstance.container = virtualContainer;
    modalRef.componentInstance.disabledScopes = {upload: true, inline: true, pdf: true, client: true, fhir: true, 'pdf-form': true};
  }

  /**
   * Adds default gdt scoring variables, if not already added.
   * Uses gdt key 8480 by default (result text)
   *
   */
  public addDefaultGdtScoringVariables() {
    if (!this.questionnaire.meta.options.gdt || !this.questionnaire.meta.options.gdt.addScoring) {
      return;
    }

    const scoringPathHashes = Object.keys(this.questionnaire.meta.options.variables['scoring'] || {}).map(varName => sha.sha256(varName));

    if (!scoringPathHashes.length) {
      return;
    }

    const gdtVariables = this.variables.getVariablesForScope('gdt');
    const flipped = this.variables.flipVariables(gdtVariables);

    for (const scoringPathHash of scoringPathHashes) {
      if (flipped[scoringPathHash]) {
        continue;
      }

      this.variables.addVariableMapping('gdt', '8480', scoringPathHash, {});
    }
  }

  /**
   * Returns true when questionnaire contains scoring variables
   * @returns {boolean}
   */
  public hasScoring(): boolean {
    const scoring = this.questionnaire.meta.options.variables['scoring'];
    return scoring && Object.keys(scoring).length > 0
  }

  /**
   * Returns scoring variables as array
   * @returns {string[]}
   */
  public getScoringVariables(): string[] {
    return Object.keys(this.questionnaire.meta.options.variables['scoring'] || {});
  }

  /**
   * Filters out non existing filter groups
   *
   * @returns {string[]}
   */
  public filterTags(tags: string[]): string[] {
    const filtered = [];
    const filtergroups = this.filtergroups ? this.filtergroups.map(fg => fg.id) : tags;

    for (const tag of tags) {
      if (filtergroups.indexOf(tag) !== -1) {
        filtered.push(tag);
      }
    }

    return filtered;
  }

  /**
   * Toggles page view mode on/off and resets to first page
   */
  public togglePageView(): void {
    this.pageViewEnabled = !this.pageViewEnabled;
    this.currentPageIndex = 0;

    if (this.pageViewEnabled) {
      this.rebuildPages();
    }
  }

  /**
   * Rebuilds the cached pages array based on pageBreak elements.
   * Call this when questions are added, removed, or reordered.
   */
  public rebuildPages(): void {
    this.pages = [];

    if (!this.questionnaire || !this.questionnaire.questions) {
      return;
    }

    const questions = this.questionnaire.questions;
    let startIndex = 0;

    for (let i = 0; i < questions.length; i++) {
      if (questions[i].format === 'pageBreak') {
        const title = questions[i].title?.[this.locale] || '';
        this.pages.push({startIndex, endIndex: i, title});
        startIndex = i + 1;
      }
    }

    // add last page if there are remaining questions after last pageBreak
    if (startIndex < questions.length) {
      this.pages.push({startIndex, endIndex: questions.length - 1, title: ''});
    }

    // ensure currentPageIndex is still valid
    if (this.currentPageIndex >= this.pages.length && this.pages.length > 0) {
      this.currentPageIndex = this.pages.length - 1;
    }

    this.rebuildVisibleQuestions();
  }

  /**
   * Rebuilds the cached visible questions array.
   * Only needed when page view is enabled.
   */
  public rebuildVisibleQuestions(): void {
    this.visibleQuestions = [];

    // in flat view, we iterate directly over questionnaire.questions in the template
    if (!this.pageViewEnabled) {
      return;
    }

    if (!this.questionnaire || !this.questionnaire.questions || this.pages.length === 0) {
      return;
    }

    if (this.currentPageIndex < 0 || this.currentPageIndex >= this.pages.length) {
      this.currentPageIndex = Math.max(0, Math.min(this.currentPageIndex, this.pages.length - 1));
    }

    const questions = this.questionnaire.questions;
    const currentPage = this.pages[this.currentPageIndex];

    if (!currentPage) {
      return;
    }

    for (let i = currentPage.startIndex; i <= currentPage.endIndex; i++) {
      const question = questions[i];
      const isFirst = i === currentPage.startIndex;
      const isLast = i === currentPage.endIndex;
      const isPageBreak = question.format === 'pageBreak';
      const nextIsPageBreak = i < currentPage.endIndex && questions[i + 1]?.format === 'pageBreak';

      this.visibleQuestions.push({
        question,
        realIndex: i,
        isFirstOnPage: isFirst,
        isLastOnPage: isLast || isPageBreak || nextIsPageBreak
      });
    }
  }

  /**
   * Handles page change from select dropdown
   *
   * @param pageIndex The new page index (as string from select2)
   */
  public onPageChange(pageIndex: string | number): void {
    this.currentPageIndex = typeof pageIndex === 'string' ? parseInt(pageIndex, 10) : pageIndex;
    this.rebuildVisibleQuestions();
  }

  /**
   * Forces Select2 to update by changing the index in the next tick.
   * This avoids Angular's ExpressionChangedAfterItHasBeenCheckedError.
   *
   * @param targetIndex The page index to select
   * @param rebuildVisible Whether to rebuild visible questions after selection
   */
  private forceSelectPage(targetIndex: number, rebuildVisible = false): void {
    this.currentPageIndex = targetIndex;
    if (rebuildVisible) {
      this.rebuildVisibleQuestions();
    }
  }

  /**
   * Navigates to next or previous page
   *
   * @param delta Direction: 1 for next, -1 for previous
   */
  public goToPage(delta: number): void {
    if (!this.canGoToPage(delta)) {
      return;
    }

    this.currentPageIndex = this.currentPageIndex + delta;
    this.rebuildVisibleQuestions();
  }

  /**
   * Checks if navigation to next/previous page is possible
   *
   * @param delta Direction: 1 for next, -1 for previous
   * @returns True if navigation is possible
   */
  public canGoToPage(delta: number): boolean {
    const newIndex = this.currentPageIndex + delta;
    return newIndex >= 0 && newIndex < this.pages.length;
  }

  /**
   * TrackBy function for ngFor to preserve DOM elements and scroll position.
   *
   * @param index The index in the array
   * @param item The visible question item
   * @returns The question id for tracking
   */
  public trackByQuestionId(index: number, item: {question: any; realIndex: number}): string {
    return item.question.id;
  }

  /**
   * TrackBy function for flat view (direct question iteration).
   *
   * @param index The index in the array
   * @param question The question container
   * @returns The question id for tracking
   */
  public trackByQuestion(index: number, question: Questionnaire.Container): string {
    return question.id;
  }

  /**
   * Returns the start index of the current page.
   * Used to map DOM indices to real questionnaire indices when in page view mode.
   *
   * @returns Start index of current page, or 0 if page view is disabled
   */
  public getPageStartIndex(): number {
    if (!this.pageViewEnabled || this.pages.length === 0) {
      return 0;
    }

    if (this.currentPageIndex >= this.pages.length) {
      return 0;
    }

    return this.pages[this.currentPageIndex].startIndex;
  }

  /**
   * Adds a new empty page after the current page.
   * If the current page has no pageBreak, one will be created first.
   */
  public addNewPage(): void {
    if (!this.pageViewEnabled || this.pages.length === 0) {
      return;
    }

    this.isPageOperationInProgress = true;
    const targetPageIndex = this.currentPageIndex + 1;

    const currentPage = this.pages[this.currentPageIndex];
    const questions = this.questionnaire.questions;
    const lastQuestion = questions[currentPage.endIndex];
    const hasPageBreak = lastQuestion?.format === 'pageBreak';

    // if current page has no pageBreak, create one first
    if (!hasPageBreak) {
      const newPageBreak = this.createPageBreakElement();
      this.state.addElement(newPageBreak, currentPage.endIndex);
      this.rebuildPages();
    }

    // show loader
    this.layoutService.showPreloader();

    // get updated page reference after potential rebuild
    const updatedPage = this.pages[this.currentPageIndex];

    // insert a new pageBreak after the current page's pageBreak
    const newPageBreak = this.createPageBreakElement();
    this.state.addElement(newPageBreak, updatedPage.endIndex);

    // rebuild pages and navigate to the new page
    this.rebuildPages();
    this.currentPageIndex = targetPageIndex;
    this.rebuildVisibleQuestions();

    // hide loader after 500ms and show success message
    setTimeout(() => {
      this.layoutService.hidePreloader();
      this.isPageOperationInProgress = false;
      alert(this._(KEYS.EDITOR.PAGE_ADDED_SUCCESSFULLY));
    }, 500);
  }

  /**
   * Copies the current page including all containers.
   * If the current page has no pageBreak, one will be created first.
   * The copied containers are inserted after the current page's pageBreak.
   */
  public copyCurrentPage(): void {
    if (!this.pageViewEnabled || this.pages.length === 0) {
      return;
    }

    this.isPageOperationInProgress = true;
    const targetPageIndex = this.currentPageIndex + 1;

    const currentPage = this.pages[this.currentPageIndex];
    const questions = this.questionnaire.questions;
    const lastQuestion = questions[currentPage.endIndex];
    const hasPageBreak = lastQuestion?.format === 'pageBreak';

    // if current page has no pageBreak, create one first
    if (!hasPageBreak) {
      const newPageBreak = this.createPageBreakElement();
      this.state.addElement(newPageBreak, currentPage.endIndex);
      this.rebuildPages();
    }

    // show loader
    this.layoutService.showPreloader();

    // get updated page reference after potential rebuild
    const updatedPage = this.pages[this.currentPageIndex];

    // collect all containers of current page
    const containersToCopy: any[] = [];
    for (let i = updatedPage.startIndex; i <= updatedPage.endIndex; i++) {
      containersToCopy.push(questions[i]);
    }

    // duplicate containers via state service. duplicateElement regenerates the
    // top-level uuid, recomputes path hashes, copies mapped variables and remaps
    // condition references so the copy is fully isolated from the source.
    const copiedContainers = containersToCopy.map(container => this.state.duplicateElement(container));

    // insert copied containers after the current page's pageBreak (in reverse order)
    const insertPosition = updatedPage.endIndex;
    for (let i = copiedContainers.length - 1; i >= 0; i--) {
      this.state.addElement(copiedContainers[i], insertPosition);
    }

    // rebuild pages and navigate to the new page
    this.rebuildPages();
    this.currentPageIndex = targetPageIndex;
    this.rebuildVisibleQuestions();

    // hide loader after 1 second and show success message
    setTimeout(() => {
      this.layoutService.hidePreloader();
      this.isPageOperationInProgress = false;
      alert(this._(KEYS.EDITOR.PAGE_COPIED_SUCCESSFULLY));
    }, 1000);
  }

  /**
   * Deletes the current page including all its containers.
   * Asks for confirmation before deleting.
   */
  public async deleteCurrentPage(): Promise<void> {
    if (!this.pageViewEnabled || this.pages.length === 0) {
      return;
    }

    // ask for confirmation
    if (!await confirm(this._(KEYS.EDITOR.CONFIRM_DELETE_PAGE))) {
      return;
    }

    this.isPageOperationInProgress = true;

    // show loader
    this.layoutService.showPreloader();

    const currentPage = this.pages[this.currentPageIndex];
    const targetPageIndex = Math.max(0, this.currentPageIndex - 1);

    // delete all containers of the current page (from end to start to preserve indices)
    for (let i = currentPage.endIndex; i >= currentPage.startIndex; i--) {
      this.state.deleteElement(i);
    }

    // rebuild pages and navigate to the appropriate page
    this.rebuildPages();
    this.currentPageIndex = Math.min(targetPageIndex, this.pages.length - 1);
    this.rebuildVisibleQuestions();

    // hide loader after 500ms and show success message
    setTimeout(() => {
      this.layoutService.hidePreloader();
      this.isPageOperationInProgress = false;
      alert(this._(KEYS.EDITOR.PAGE_DELETED_SUCCESSFULLY));
    }, 500);
  }

  /**
   * Creates a new pageBreak element with a new UUID.
   *
   * @returns A new pageBreak container
   */
  private createPageBreakElement(): Questionnaire.Container {
    const pageBreak = JSON.parse(JSON.stringify(Question.Structure.PAGE_BREAK));
    pageBreak.id = this.editor.generateUUID();
    pageBreak.title = {[this.locale]: ''};
    return pageBreak;
  }

  /**
   * Asks user for confirmation when navigating away with unsaved changes
   * @returns {Promise<boolean>} true if user confirms navigation, false if they want to stay
   */
  public async confirmNavigation(): Promise<boolean> {
    if (this.isDirty() && this.questionnaire.questions.length > 0) {
      return await confirm(this._(KEYS.EDITOR.ASK_CANCEL_EDITING));
    }
    // No unsaved changes, allow navigation
    return true;
  }

  /**
   * CanDeactivate guard implementation - called by Angular Router before navigation
   * @returns {Promise<boolean>} true if navigation is allowed, false if prevented
   */
  canDeactivate(): Promise<boolean> {
    return this.confirmNavigation();
  }

  /**
   * Starts new questionnaire session. Creates snapshot, so we can detect
   * if questionnaire contains unsaved changes
   *
   * @param {Questionnaire} questionnaire
   */
  private startSession(questionnaire: Questionnaire) {
    this.questionnaire = questionnaire;
    this.questionnaireSnapshot = JSON.stringify(questionnaire);

    if (this.pageViewEnabled) {
      this.rebuildPages();
    }
  }

  /**
   * Returns true if questionnaire contains unsaved content
   *
   * @returns {boolean}
   */
  private isDirty() {
    const current = JSON.stringify(this.questionnaire);
    return current != this.questionnaireSnapshot;
  }

  /**
   * Memorizes current questionnaire, it has questions and contains unsaved content
   */
  private memorizeSession() {
    // memorize questionnaire, if it has questions and contains unsaved content
    const isDirty = this.questionnaire.questions.length && this.isDirty();
    isDirty && this.editor.memorizeQuestionnaire(this.questionnaire);
  }

  /**
   * Checks if a cached questionnaire is present and ask's user to resume it
   */
  private askResumeSession() {
    // early bail out if no memorized questionnaire present
    if (!this.editor.hasMemorizedQuestionnaire()) {
      return;
    }

    setTimeout(() => {
      (async () => {
        try {
          const resume = await confirm(this._(KEYS.EDITOR.ASK_RESUME_QUESTIONNAIRE));
          resume ? this.startSession(await this.editor.resumeMemorizedQuestionnaire()) : this.editor.deleteMemorizedQuestionnaire();

          if (resume) {
            this.layoutService.showPreloader();

            // previous session could contain an already deleted questionnaire. therefore we ask the api if the questionnaire exists
            let existsInDb = this.questionnaire.createdAt ? !!await this.api.hasQuestionnaire(this.questionnaire.id) : false;

            const path = window.location.pathname;
            const replaceValue = existsInDb ? `/editor/${this.questionnaire.id}` : '/editor';
            const newPath = path.replace(/\/editor($|\/.*$)/, replaceValue);
            window.history.pushState(null, null, newPath);

            this.editMode = existsInDb;
            !existsInDb && (this.questionnaire.revision = 1);
            setTimeout(() => this.layoutService.hidePreloader(), 1000);
          }
        } catch (error) {
          console.error(error);
        }
      })();
    }, 50);
  }

  /**
   * Applies license constrictions
   */
  private applyRestrictions() {
    const numQuest = this.configService.get('constraint.numQuestionnaires');

    if (!numQuest) {
      return;
    }

    const filterByUser = this.isSimu;
    this.api.getNumQuestionnaires(filterByUser).then(count => {
      if (count >= numQuest) {
        alert(this._(KEYS.EDITOR.QUESTIONNAIRE_LIMIT_REACHED));
        this.location.back();
      }
    })

    this.editor.loadFiltergroups().then(filtergroups => {
      this.filtergroups = filtergroups;
      const filterGroupsAvailable = this.filtergroups.length;
      const questionnaireHasNoFilterGroups = !(this.questionnaire.meta.tags || []).length;

      if (this.isSimu && filterGroupsAvailable && questionnaireHasNoFilterGroups) {
        const firstFilterGroup = this.filtergroups[0];
        this.questionnaire.meta.tags = [firstFilterGroup.id];
      }
    });
  }

  private applySimuRestrictions() {
    this.editor.loadFiltergroups().then(filtergroups => {
      this.filtergroups = filtergroups;
      const hasNoFiltergroups = this.filterTags(this.questionnaire.meta.tags || []).length === 0;

      if (this.isSimu && hasNoFiltergroups && this.editMode) {
        alert(this._(KEYS.EDITOR.QUESTIONNAIRE_NO_PERMISSION));
        this.location.back();
        return;
      }
    });
  }
}
